import { Alert, AlertIcon, Box, Button, Card, CardBody, Checkbox, Heading, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  ContributingEntityStatus,
  type ContributingEntityDetailDto,
  type EspdResponseDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { ContributionValidationIssues } from "../ContributionValidationIssues";

interface Props {
  detail: ContributingEntityDetailDto;
  espd: EspdResponseDto;
  onChange: (next: EspdResponseDto) => void;
  readOnly: boolean;
  isSubmitting: boolean;
  isSaving: boolean;
  onSubmit: () => void;
  previousStep?: () => void;
}

export const ContributionReviewStep = ({
  detail,
  espd,
  onChange,
  readOnly,
  isSubmitting,
  isSaving,
  onSubmit,
  previousStep,
}: Props) => {
  const { t } = useTranslation();
  const isComplete = detail.entity.status === ContributingEntityStatus.ResponseValidated;
  const declarationConfirmed = espd.finalDeclaration?.confirmed ?? false;

  return (
    <Box pb="8" pt="6">
      <Heading size="md" mb="6">
        {t("Summary and submission")}
      </Heading>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Checkbox
            isDisabled={readOnly}
            isChecked={declarationConfirmed}
            onChange={(e) => onChange({ ...espd, finalDeclaration: { confirmed: e.target.checked } })}
          >
            <Text fontSize="sm">{t("I confirm that the information above is accurate and complete.")}</Text>
          </Checkbox>
        </CardBody>
      </Card>

      {isComplete ? (
        <Alert status="success" borderRadius="lg" mb="8">
          <AlertIcon />
          <Text fontSize="sm">
            {t("Your answer is complete and has been shared with {{leadSupplier}}.", {
              leadSupplier: detail.context.leadSupplierName,
            })}
          </Text>
        </Alert>
      ) : (
        <Stack spacing="4" mb="8">
          <ContributionValidationIssues issues={detail.entity.validationIssues ?? []} />
          {!readOnly && (
            <Box>
              <Button
                colorScheme="teal"
                onClick={onSubmit}
                isLoading={isSubmitting}
                isDisabled={isSaving || !declarationConfirmed}
                title={declarationConfirmed ? undefined : (t("The final declaration must be confirmed.") ?? undefined)}
              >
                {t("Submit answer")}
              </Button>
            </Box>
          )}
        </Stack>
      )}

      <StepNavigation onPrevious={previousStep} />
    </Box>
  );
};
